import { LitElement, html } from 'lit';
import { customElement, query, state, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import { formatDate } from '@amityeko/pbc-core/dist/utilities/datetime';
import { withAuth } from '@amityeko/pbc-core/dist/providers/auth/consumer-mixin';

import '@amityeko/design-system/dist/elements/typography/tg-title';
import '@amityeko/design-system/dist/elements/typography/tg-body-two';
import '@amityeko/design-system/dist/elements/dialog';
import '@amityeko/design-system/dist/elements/button';
import '@amityeko/design-system/dist/elements/accordion-item';
import type AmityekoDialog from '@amityeko/design-system/dist/elements/dialog';

import '../card';
import type { CardList } from '../card-list/types';

import { Sender, RecognitionDetails, RecognitionDetail } from './types';

import styles from './styles';

/**
 * @dependency amityeko-dialog
 * @dependency amityeko-tg-title
 * @dependency amityeko-tg-body-two
 * @dependency amityeko-accordion-item
 * @dependency amityeko-button
 *
 * @slot default - Element that triggers the dialog with Empty View
 */
@customElement('rnr-recognition-list-per-user')
export default class RnRRecognitionListPerUser extends withAuth(LitElement) {
  static styles = styles;

  @query('#dialog') dialog!: AmityekoDialog;

  @property()
  receiverId?: string;

  @state()
  recognitionSummary?: CardList;

  @state()
  senderList?: Sender[];

  @state()
  recognitionDetails?: RecognitionDetails;

  public async show(card: any = {}) {
    this.dialog.show();

    if (Object.keys(card).length > 0) {
      this.recognitionSummary = card;

      if (!this.auth?.http || !this.receiverId) return;

      try {
        const response = await this.auth.http.get(
          `/api/v1/recognitions/achievements/badges/${this.recognitionSummary?.id}/senders`,
          { receiverId: this.receiverId, limit: 5 },
        );

        this.senderList = response.data
          ?.map((data: any) => {
            if (Object.keys(data?.sender).length > 0) {
              return { ...data?.sender };
            }
          })
          .filter((data: any) => data !== undefined);
      } catch (e: any) {
        emit(this, 'amityeko-notification--toast', {
          detail: { message: e?.error?.message, variant: 'error' },
        });
      }
    }
  }

  private _reset() {
    //clear current state;
    this.recognitionDetails = undefined;
    this.recognitionSummary = undefined;
    this.senderList = undefined;
  }

  private _onLoadMoreClick() {
    console.log('Load more');
  }

  private _onTriggerClick() {
    this.dialog.show();
  }

  private async _onClickAccordionItem(senderId: string) {
    if (!this.auth?.http || !this.receiverId) return;

    try {
      if (this.recognitionDetails) {
        const existedSenderId = Object.keys(this.recognitionDetails).find(id => id === senderId);

        // if data is already existed, make sure don't call api again
        if (existedSenderId) return;
      }

      const response = await this.auth.http.get(
        `/api/v1/recognitions/achievements/badges/${this.recognitionSummary?.id}/senders/${senderId}`,
        { receiverId: this.receiverId },
      );

      // concat all data with the related senderId
      this.recognitionDetails = { ...this.recognitionDetails, [senderId]: [...response?.data] };
    } catch (e: any) {
      emit(this, 'amityeko-notification--toast', {
        detail: { message: e?.error?.message, variant: 'error' },
      });
    }
  }

  private _onDialogBack() {
    this.dialog.hide();
    this._reset();
    emit(this, 'rnr-dialog-back');
  }

  private _onClickRecognitionItem(recognitionDetail: RecognitionDetail) {
    this.dialog.hide();
    emit(this, 'rnr-select-recognition-item', { detail: { ...recognitionDetail } });
  }

  protected render() {
    return html`
      <slot @click=${this._onTriggerClick}></slot>
      <amityeko-dialog
        id="dialog"
        @amityeko-back=${this._onDialogBack}
        @amityeko-cancel=${this._reset}
        ?hasBackButton=${true}
      >
        <div class="content">
          <div class="card-summary">
            <img src=${this.recognitionSummary?.imageSrc || ''} />
            <amityeko-tg-title>${this.recognitionSummary?.cardTitle}</amityeko-tg-title>
            <div class="card-summary-content">
              <amityeko-tg-body-two>${this.recognitionSummary?.count} items</amityeko-tg-body-two>
              <amityeko-tg-body-two>${this.recognitionSummary?.points} Points</amityeko-tg-body-two>
            </div>
          </div>
          <div class="recognition-list">
            ${this.senderList
              ? repeat(
                  this.senderList,
                  data => data?.id,
                  data => html`
                    <!--TODO: Add image url that is not currently in api-->
                    <amityeko-accordion-item
                      image="https://i.ibb.co/k3X2GfC/card1.png"
                      .label=${data?.displayName}
                      @amityeko-select-accordion-item=${() => this._onClickAccordionItem(data?.id)}
                    >
                      ${this.recognitionDetails?.[data?.id]
                        ? html`
                            <div slot="content">
                              ${repeat(
                                this.recognitionDetails?.[data?.id],
                                detail => detail?.id,
                                detail => html`
                                  <div
                                    class="recoginition-list-item"
                                    @click=${() => this._onClickRecognitionItem(detail)}
                                  >
                                    <div class="recoginition-item-block"></div>
                                    <amityeko-tg-body-two>
                                      Sent on ${formatDate(detail?.createdAt)}
                                    </amityeko-tg-body-two>
                                  </div>
                                `,
                              )}
                            </div>
                          `
                        : null}
                    </amityeko-accordion-item>
                  `,
                )
              : null}
          </div>

          <!--TODO: add pagination logic -->
          <!-- ${this.senderList
            ? html`
                <amityeko-button @click=${this._onLoadMoreClick} variant="ghost">
                  Load more
                </amityeko-button>
              `
            : null} -->
        </div>
      </amityeko-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-recognition-list-per-user': RnRRecognitionListPerUser;
  }
}
